import React, { useState } from "react";
import {
  Button,
  CssBaseline,
  TextField,
  Typography,
  Box,
  Container,
  Checkbox,
  FormControlLabel,
  Link,
  InputAdornment,
  IconButton,
  Paper,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  D_INV22G,
  SGE_MODULE_NAME,
  TOKEN,
  USER,
} from "constants/api/localStorage";
import { Login } from "models/Resources/Login";
import { useAppDispatch } from "hooks/StoreHooks";
import { setToken, setUser } from "store/Slices/appStateSlice";
import { connectionApi } from "services/api/connectionApi";
import { loginResponseToLoginResource } from "services/responseConverters/loginResponseToLoginResource";
import { mainTheme } from "constants/style/theme/appTheme";
import { ConnectionResult } from "./ConnectionResult";
import ConnectionErrorMessage from "./ConnectionErrorMessage";

import "./ConnectionPage.scss";

const ConnectionPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [loginMutation] = connectionApi.endpoints.login.useMutation();

  const [username, setUsername] = useState(
    localStorage.getItem(USER) ? localStorage.getItem(USER) : ""
  );
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberUser, setRememberUser] = useState(
    localStorage.getItem(USER) ? true : false
  );
  const [connectionStatus, setConnectionResult] = useState(
    ConnectionResult.NO_ATTEMPT
  );

  const onChangeUsername = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setUsername(e.target.value);
  };

  const onChangePassword = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setPassword(e.target.value);
  };

  const handleClickShowPassword = () => setShowPassword(show => !show);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const result: Login = loginResponseToLoginResource(
        await loginMutation({
          user: username,
          password,
        }).unwrap()
      );
      const d_inv22g = result.authorizations.find(x => x.functName == D_INV22G);
      if (result.success && d_inv22g) {
        if (d_inv22g.canRead && d_inv22g.module.includes(SGE_MODULE_NAME)) {
          setConnectionToValid(result);
        } else {
          setConnectionResult(ConnectionResult.CANT_READ);
        }
      } else {
        dispatch(connectionApi.util.resetApiState());
        setConnectionResult(ConnectionResult.INVALID_LOGIN);
      }
    } catch (error) {
      setConnectionResult(ConnectionResult.INVALID_LOGIN);
      console.error("Error: ", error);
    }
  };

  function setConnectionToValid(result: Login) {
    setConnectionResult(ConnectionResult.NO_ATTEMPT);
    if (rememberUser) {
      localStorage.setItem(TOKEN, result.token);
      localStorage.setItem(USER, result.user);
    } else {
      localStorage.removeItem(USER);
    }
    dispatch(setUser(result.user));
    dispatch(setToken(result.token));
  }

  return (
    <ThemeProvider theme={mainTheme}>
      <CssBaseline />
      <Box className="connection-page-root">
        <Box className="main-content-area">
          <Container maxWidth="sm" sx={{ p: 0 }}>
            <Typography component="h1" className="welcome-title">
              Bienvenue sur votre <br />
              gestionnaire d&apos;entrepôt
            </Typography>

            <Paper className="login-form-card">
              <Typography component="h2" className="form-title">
                CONNEXION
              </Typography>

              {connectionStatus != ConnectionResult.NO_ATTEMPT && (
                <ConnectionErrorMessage connectionResult={connectionStatus} />
              )}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  required
                  id="username"
                  label="Identifiant utilisateur:"
                  name="user"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={onChangeUsername}
                  placeholder="Identifiant"
                  slotProps={{
                    inputLabel: { shrink: true },
                  }}
                />
                <TextField
                  required
                  name="password"
                  label="Mot de passe"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={onChangePassword}
                  placeholder="Mot de passe"
                  slotProps={{
                    inputLabel: { shrink: true },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 1.5,
                    mb: 2.5,
                    flexWrap: "wrap",
                    gap: "10px",
                  }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        value="remember"
                        color="primary"
                        checked={rememberUser}
                        onChange={e => setRememberUser(e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: "0.875rem" }}>
                        Se souvenir de moi
                      </Typography>
                    }
                  />
                </Box>
                <Button
                  className="connect-button"
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    mt: 1,
                    mb: 1,
                  }}>
                  Se connecter
                </Button>
              </Box>
            </Paper>

            <Box className="decorative-logo-bottom-left" />

            <Typography variant="body1" className="support-text-custom">
              Vous éprouvez des difficultés à vous connecter?{<br />}
              Contactez{" "}
              <Link
                href="mailto:support@tanguay.ca"
                sx={{
                  color: "black",
                }}>
                support@tanguay.ca
              </Link>
            </Typography>
          </Container>
        </Box>
        <Box className="page-footer-custom">
          <Typography variant="body2">
            © {new Date().getFullYear()} Groupe BMTC Inc. Tous droits réservés.
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ConnectionPage;
